import Stripe from "stripe";
import { PaymentIntentMetadata } from "../../stripe";
import { DateTime } from "../../util/DateTime/DateTime";
import { BillingPeriod } from "./BillingPeriod";

export class Charge {
    static fromStripeCharge(charge: Stripe.Charge, paymentMethods: Stripe.PaymentMethod[]): Charge.JSON {
        return {
            id: charge.id,
            paymentId: typeof charge.payment_intent === 'string' ? charge.payment_intent : charge.payment_intent.id,
            amount: charge.amount / 100, // convert cents to dollars
            _amountReceived: (charge.amount_captured - charge.amount_refunded) / 100, // convert cents to dollars
            createdDate: new DateTime(charge.created * 1000), // convert seconds to milliseconds
            description: charge.description || "",
            metadata: charge.metadata as Record<PaymentIntentMetadata, string>,
            status: charge.status,
            paymentMethod: paymentMethods?.find((pm) => pm.id === charge.payment_method)?.type || "unknown",
        };
    }

    static new(p: Charge | Charge.JSON, billingPeriod: BillingPeriod): Charge {
        if (p instanceof Charge) {
            return p;
        }
        const pmt = new Charge(
            p.id,
            p.paymentId,
            new DateTime(p.createdDate),
            p.description || "",
            p.metadata as Record<PaymentIntentMetadata, string>,
            p.status,
            p.paymentMethod,
            p.amount
        );
        pmt._amountReceived = p._amountReceived;
        pmt.billingPeriod = billingPeriod;

        return pmt;
    }

    billingPeriod: BillingPeriod;
    private _amountReceived: number;

    private constructor(
        readonly id: string,
        readonly paymentId: string,
        readonly createdDate: DateTime,
        readonly description: string,
        readonly metadata: Record<PaymentIntentMetadata, string>,
        readonly status: Stripe.Charge.Status,
        readonly paymentMethod: Stripe.PaymentMethod.Type | "unknown",
        readonly amount: number
    ) {}

    get amountReceived(): number {
        if (this.status === "pending") {
            return this.amount;
        }
        return this._amountReceived;
    }

    get dueDate(): DateTime {
        const rawDueDate = this.metadata[PaymentIntentMetadata.billingPeriodDueDate];
        if (!rawDueDate) {
            return this.billingPeriod?.dueDate || new DateTime();
        }
        return DateTime.fromString(rawDueDate, "YYYY-MM-DD");
    }

    json(): Charge.JSON {
        return {
            id: this.id,
            paymentId: this.paymentId,
            createdDate: this.createdDate,
            description: this.description,
            metadata: this.metadata,
            status: this.status,
            amount: this.amount,
            paymentMethod: this.paymentMethod,
            _amountReceived: this._amountReceived,
        };
    }
}

export namespace Charge {
    export interface JSON {
        readonly id: string;
        readonly paymentId: string;
        readonly createdDate: DateTime;
        readonly description: string;
        readonly metadata: Record<PaymentIntentMetadata, string>;
        readonly status: Stripe.Charge.Status;
        readonly amount: number;
        readonly paymentMethod: Stripe.PaymentMethod.Type | "unknown";
        readonly _amountReceived: number;
    }
}
